import type { ComponentProps } from 'react';
import { Navigate } from 'react-router-dom';

import { InstitutionFuturesProgressStep } from '@/context/progress';
import { type RouteConfig, RouteConfigProvider, useRouteConfig } from '@/context/route-config';
import type FuturesAppropriatenessTestLayout from '@/features/futures/layouts/appropriateness-test';
import { isExemptFromAppropriatenessTest } from '@/features/institution/utils';
import { useProfileClientCategorisationTreatment } from '@/hooks/queries/use-profile-client-categorisation-treatment';
import { useProfileFuturesAppropriateness } from '@/hooks/queries/use-profile-futures-appropriateness';
import { useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';
import FuturesAppropriatenessTestResultFailedPage from '@/pages/futures/appropriateness-test-result-failed';
import FuturesAppropriatenessTestResultManualReviewPage from '@/pages/futures/appropriateness-test-result-manual-review';
import InstitutionFuturesAppropriatenessTestResultPassedPage from '@/pages/institution/futures/appropriateness-test-result-passed';
import LoadingPage from '@/pages/loading';
import { FuturesAppropriatenessResultStatus } from '@/services/api/profile/futures-appropriateness';

const ID = 'institution-futures-result-page';

const LAYOUT_PROPS: ComponentProps<typeof FuturesAppropriatenessTestLayout> = {
  id: ID,
  step: InstitutionFuturesProgressStep.APPROPRIATENESS_TEST,
};

export interface FuturesAppropriatenessTestResultRouteConfig extends RouteConfig {
  appropriatenessTest: string;
}

const InstitutionFuturesAppropriatenessTestResultContent = () => {
  const {
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const { isLoading: isLoadingClientCategorisationTreatment, data: clientCategorisationTreatment } = useProfileClientCategorisationTreatment();
  const { isLoading: isLoadingFuturesAppropriateness, data: futuresAppropriateness } = useProfileFuturesAppropriateness();
  const isLoading = isLoadingInstitutionDetailsMeta || isLoadingClientCategorisationTreatment || isLoadingFuturesAppropriateness;

  const routeConfig = useRouteConfig<FuturesAppropriatenessTestResultRouteConfig>();

  if (isLoading) return <LoadingPage />;

  if (isRegulated && !clientCategorisationTreatment) return <Navigate to="/institution/futures/client-categorisation/treatment" replace />;

  if (isExemptFromAppropriatenessTest(isRegulated, clientCategorisationTreatment)) return <InstitutionFuturesAppropriatenessTestResultPassedPage {...LAYOUT_PROPS} />;

  if (!futuresAppropriateness || !('result' in futuresAppropriateness)) return <Navigate to={routeConfig.appropriatenessTest} replace />;

  return {
    [FuturesAppropriatenessResultStatus.PASSED]: <InstitutionFuturesAppropriatenessTestResultPassedPage {...LAYOUT_PROPS} />,
    [FuturesAppropriatenessResultStatus.MANUAL_REVIEW]: <FuturesAppropriatenessTestResultManualReviewPage {...LAYOUT_PROPS} />,
    [FuturesAppropriatenessResultStatus.FAILED]: <FuturesAppropriatenessTestResultFailedPage {...LAYOUT_PROPS} />,
  }[futuresAppropriateness.result.status];
};
InstitutionFuturesAppropriatenessTestResultContent.displayName = 'InstitutionFuturesAppropriatenessTestResultContent';

function InstitutionFuturesAppropriatenessTestResultPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/institution/futures/appropriateness-test',
      }}
    >
      <InstitutionFuturesAppropriatenessTestResultContent />
    </RouteConfigProvider>
  );
}
InstitutionFuturesAppropriatenessTestResultPage.displayName = 'InstitutionFuturesAppropriatenessTestResultPage';

export default InstitutionFuturesAppropriatenessTestResultPage;
